import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Video, Radio, Calendar, Users, Sparkles, Library,
  PlusCircle, Hash, Compass, Heart, ArrowRight,
} from "lucide-react";
import { api } from "../../lib/api";
import DhammaAssistantPanel from "../../components/DhammaAssistantPanel";

const QUICK_ACTIONS = [
  { to: "/platform/meetings", label: "Create Meeting", icon: Video, sub: "Host an invite-only Sangha call", color: "from-[#2D5BFF] to-[#6C4EFF]" },
  { to: "/platform/meetings", label: "Join Meeting", icon: Hash, sub: "Enter with a meeting code", color: "from-[#0B1F4D] to-[#2D5BFF]" },
  { to: "/platform/calendar", label: "Schedule Event", icon: Calendar, sub: "Plan your next gathering", color: "from-[#6C4EFF] to-[#9B59FF]" },
  { to: "/platform/live", label: "Live Stream", icon: Radio, sub: "Watch global broadcasts", color: "from-[#D4A64A] to-[#B8862E]" },
  { to: "/platform/meditation", label: "Meditation Room", icon: Compass, sub: "Silent sitting · ambient", color: "from-[#1a8a4f] to-[#0d5b35]" },
  { to: "/platform/library", label: "Dhamma Library", icon: Library, sub: "Books · suttas · talks", color: "from-[#0B1F4D] to-[#1a2f6b]" },
];

const VALUES = [
  { kicker: "Pragya", label: "Wisdom" },
  { kicker: "Karuna", label: "Compassion" },
  { kicker: "Samata", label: "Equality" },
  { kicker: "Bandhutva", label: "Fraternity" },
];

export default function PlatformHome() {
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get("/events?upcoming_only=true").then((r) => alive && setEvents(r.data.slice(0, 3))).catch(() => {});
    return () => { alive = false; };
  }, []);

  const heroLive = events[0];

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-[1600px] mx-auto" data-testid="platform-home">
      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0B1F4D] via-[#1a2f6b] to-[#2D5BFF] text-pearl p-8 shadow-xl border border-white/10">
          <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(212,166,74,0.4) 0%, transparent 50%)' }} />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-500/90 text-white text-[10px] uppercase tracking-widest rounded-full font-semibold">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> Live
              </span>
              <span className="text-xs uppercase tracking-[0.25em] text-pearl/60">
                Global Sangha · Multilingual
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-pearl leading-tight">
              {heroLive ? heroLive.title : "Welcome to the Ambedkarite Buddhist Global Network UK"}
            </h1>
            <p className="mt-4 text-pearl/80 text-base max-w-2xl leading-relaxed italic">
              "Cultivation of mind should be the ultimate aim of human existence."
              <span className="block mt-1 not-italic text-pearl/50 text-sm">— Dr. B. R. Ambedkar</span>
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/platform/meetings"
                data-testid="hero-start-meeting"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron/90 transition-colors"
              >
                <Video size={16} /> Open meetings
              </Link>
              <Link
                to="/platform/live"
                data-testid="hero-watch-live"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 border border-white/20 text-pearl rounded-md text-sm hover:bg-white/15"
              >
                <Radio size={16} /> Watch live
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="font-display text-3xl text-saffron">120K+</div>
                <div className="text-[10px] uppercase tracking-widest text-pearl/60">Members</div>
              </div>
              <div>
                <div className="font-display text-3xl text-saffron">75+</div>
                <div className="text-[10px] uppercase tracking-widest text-pearl/60">Countries</div>
              </div>
              <div>
                <div className="font-display text-3xl text-saffron">500+</div>
                <div className="text-[10px] uppercase tracking-widest text-pearl/60">Groups</div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Dhamma Assistant on the right */}
        <div className="lg:col-span-1">
          <DhammaAssistantPanel />
        </div>
      </section>

      {/* Quick actions grid */}
      <section className="mb-10">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-display text-2xl text-navy">Quick Actions</h2>
            <p className="text-sm text-navy/60">Start a meeting, join a stream, open the library, all in one click.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {QUICK_ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.label}
                to={a.to}
                data-testid={`quick-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group rounded-xl bg-white border border-navy/10 p-5 hover:border-saffron transition-colors hover:-translate-y-0.5 shadow-sm hover:shadow-md"
              >
                <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${a.color} flex items-center justify-center text-white mb-3`}>
                  <Icon size={20} />
                </div>
                <div className="font-display text-sm text-navy">{a.label}</div>
                <div className="text-[11px] text-navy/55 mt-0.5 leading-snug">{a.sub}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Upcoming + Core values */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-navy/10 rounded-xl p-6 shadow-sm" data-testid="platform-upcoming-events">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl text-navy">Upcoming Events</h2>
            <Link to="/events" className="text-xs uppercase tracking-widest text-saffron hover:text-saffron-light">View all</Link>
          </div>
          {events.length === 0 ? (
            <div className="text-sm text-navy/60 py-4">No upcoming events scheduled yet.</div>
          ) : (
            <div className="space-y-3">
              {events.map((e, idx) => {
                const eid = e.id || e._id || idx;
                return (
                  <Link
                    key={eid}
                    to={`/events/${eid}`}
                    className="flex gap-4 p-3 rounded-lg hover:bg-pearl border border-transparent hover:border-navy/10"
                    data-testid={`platform-event-${eid}`}
                  >
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-saffron/20 to-saffron/5 flex flex-col items-center justify-center flex-shrink-0">
                      <div className="text-xs uppercase tracking-widest text-saffron">
                        {e.date ? new Date(e.date).toLocaleDateString("en-GB", { month: "short" }) : "TBD"}
                      </div>
                      <div className="text-xl font-display text-navy">
                        {e.date ? new Date(e.date).getDate() : "—"}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-base text-navy truncate">{e.title}</div>
                      <div className="text-xs text-navy/60 mt-0.5">
                        {e.location || "Location TBD"} · {e.mode || e.format || "in-person"}
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-navy/40 self-center" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl p-6 shadow-sm text-pearl bg-gradient-to-br from-[#0B1F4D] via-[#1a2f6b] to-[#6C4EFF] relative overflow-hidden border border-white/10" data-testid="platform-core-values">
          <h3 className="font-display text-xl text-pearl mb-1">Our Core Values</h3>
          <p className="text-pearl/70 text-xs uppercase tracking-[0.2em]">Pillars of our Sangha</p>
          <div className="mt-6 space-y-4">
            {VALUES.map((v) => (
              <div key={v.kicker} className="flex items-baseline justify-between border-b border-white/10 pb-3">
                <div>
                  <div className="font-display text-lg text-pearl">{v.kicker}</div>
                  <div className="text-[11px] uppercase tracking-widest text-saffron/80">{v.label}</div>
                </div>
                <Sparkles size={14} className="text-saffron/60" />
              </div>
            ))}
          </div>
          <p className="mt-6 text-[11px] italic text-pearl/60 leading-relaxed">
            Buddha Dhamma is humanity's message of peace and equality.
            <span className="block mt-1 not-italic text-pearl/40">— Dr. B. R. Ambedkar</span>
          </p>
        </div>
      </section>
    </div>
  );
}
